import { useEffect, useRef } from 'react';
import { io, Socket } from 'socket.io-client';
import { useAuthContext } from '../contexts/AuthContext';
import config from '../config';

interface OmnichannelEvent {
  type:
    | 'new_message'
    | 'new_conversation'
    | 'conversation_updated'
    | 'message_status_updated'
    | 'form_submitted';
  data: {
    conversation_id?: string;
    message_id?: string;
    conversation?: any;
    message?: any;
    form_submission?: any;
    submitted_at?: string;
  };
  timestamp: string;
}

interface UseOmnichannelWebSocketOptions {
  onNewMessage?: (event: OmnichannelEvent) => void;
  onConversationUpdated?: (event: OmnichannelEvent) => void;
  enabled?: boolean;
}

export function useOmnichannelWebSocket(options: UseOmnichannelWebSocketOptions = {}) {
  const { onNewMessage, onConversationUpdated, enabled = true } = options;
  const { user } = useAuthContext();
  const socketRef = useRef<Socket | null>(null);
  const callbacksRef = useRef({ onNewMessage, onConversationUpdated });

  // Update callbacks ref without reconnecting
  useEffect(() => {
    callbacksRef.current = { onNewMessage, onConversationUpdated };
  }, [onNewMessage, onConversationUpdated]);

  useEffect(() => {
    if (!enabled || !user) {
      return;
    }

    // Get tenant ID from localStorage
    const tenantId = localStorage.getItem('tenantId');
    if (!tenantId) {
      return;
    }
    
    // Connect to WebSocket server using the same config as notifications
    const socket = io(config.WS_URL, {
      withCredentials: true,
      transports: ['websocket', 'polling'],
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
      timeout: 10000,
      auth: {
        token: localStorage.getItem('token'),
      },
      query: {
        tenantId: tenantId
      }
    });

    socketRef.current = socket;

    socket.on('connect', () => {
      socket.emit('authenticate', { user, tenantId });
    });

    socket.on('omnichannelEvent', (event: OmnichannelEvent) => {

      switch (event.type) {
        case 'new_message':
          callbacksRef.current.onNewMessage?.(event);
          break;
        case 'conversation_updated':
          callbacksRef.current.onConversationUpdated?.(event);
          break;
        case 'form_submitted':
          callbacksRef.current.onNewMessage?.(event);
          break;
        default:
          console.log('[Omnichannel WS] Unhandled event type:', event.type);
      }
    });

    socket.on('connect_error', (error) => {
      console.error('[Omnichannel WS] ❌ Connection error:', error);
    });

    return () => {
      socket.disconnect();
      socketRef.current = null;
    };
  }, [enabled, user]);

  return {
    socket: socketRef.current,
    isConnected: socketRef.current?.connected || false,
  };
}
